import Alert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import {
  useLazyContext,
  useRequiredContext,
} from "@redotech/react-util/context";
import { useInput } from "@redotech/react-util/form";
import { useLoad, useTriggerLoad } from "@redotech/react-util/load";
import { uploadFile } from "@redotech/redo-api-client/conversations";
import { getCustomerSearch } from "@redotech/redo-merchant-app-common/client/shopify";
import {
  Attachment,
  CreateConversationBody,
} from "@redotech/redo-model/create-conversation-body";
import {
  Macro,
  MacroAutomationsData,
  isAtLeastOneMacroAutomationActive,
} from "@redotech/redo-model/macro";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Autocomplete } from "@redotech/redo-web/autocomplete";
import PaperclipIcon from "@redotech/redo-web/icon-old/paperclip.svg";
import { QuillEditor } from "@redotech/redo-web/quill/quill-editor";
import { ValidationError, groupInput, input } from "@redotech/ui/form";
import Quill from "quill";
import { memo, useContext, useEffect, useRef, useState } from "react";
import { useDebounce } from "usehooks-ts";
import { ConversationDetailsOrders } from "./conversation-detail-panel/conversation-details-orders";
import { MacroModal } from "./macros/macro-modal";
import * as newMessageModalCss from "./new-message-modal.module.css";
// Styles for quill editor
import * as amplitude from "@amplitude/analytics-browser";
import { emailIsValid } from "@redotech/email";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { RedoMerchantClientContext } from "@redotech/redo-merchant-app-common/client/context";
import { getCustomer } from "@redotech/redo-merchant-app-common/client/customer";
import {
  getTeamOrders,
  getTeamReturns,
} from "@redotech/redo-merchant-app-common/client/team";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import {
  ConversationPlatform,
  ExpandedConversation,
  MessageVisibility,
} from "@redotech/redo-model/conversation";
import {
  getPrimaryCustomerEmail,
  getPrimaryCustomerPhoneNumber,
} from "@redotech/redo-model/customer";
import { SupportPermissions } from "@redotech/redo-model/permissions";
import { ReplyNoDraftBody } from "@redotech/redo-model/reply-no-draft-body";
import { conversationFileUploadErrorMessages } from "@redotech/redo-model/support/conversations/conversation-file-upload-error";
import { EmailReplyType } from "@redotech/redo-model/support/conversations/email-info";
import { alertOnFailure, toast } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoTagInputField } from "@redotech/redo-web/arbiter-components/input/redo-tag-input-field";
import { RedoSpinner } from "@redotech/redo-web/arbiter-components/loading/redo-spinner";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import { CollapseSubsection } from "@redotech/redo-web/card";
import { RedoClientContext } from "@redotech/redo-web/client";
import { Divider } from "@redotech/redo-web/divider";
import { Flex } from "@redotech/redo-web/flex";
import { clearFormattingFromMacroAutomationsText } from "@redotech/redo-web/macro-variables";
import { QuillAttachmentCarousel } from "@redotech/redo-web/quill/quill-attachment-carousel";
import { QuillToolbarOptions } from "@redotech/redo-web/quill/quill-toolbar-options";
import { QuillToolbarUploadFile } from "@redotech/redo-web/quill/quill-toolbar-upload-file";
import { Text } from "@redotech/redo-web/text";
import { unique } from "@redotech/util/array";
import { convertArbitraryPhoneNumberToE164 } from "@redotech/util/phone-number";
import "quill/dist/quill.snow.css";
import { createConversation, replyNoDraft } from "./client/conversations";
import { materializeMacroIntoPendingAutomations } from "./macros/macro-automation-utils";
import { MacroAutomationsList } from "./macros/macro-automations-list";
import { performMacroAutomationsAfterSendingMessage } from "./macros/perform-macro-automations";
import { EmailIntegrationsContext } from "./services/email-integrations";
import { MacrosContext } from "./services/macros-service";
import { doFileDrop } from "./utils";

const NAME_EMAIL_DELIMITER = " - ";

function getEmails(emails: string): string[] {
  return emails.split(",").filter((email) => email.length > 0);
}

/**
 * Extracts the email address from a customerContactDisplayValue that may be in the format
 * "<name> - <email>" or just an email address.
 *
 * This function expects the input customerContactDisplayValue to contain a valid email address.
 *
 * @param customerContactDisplayValue The customerContactDisplayValue that may contain a delimiter
 * @returns The email address, or the original value if no delimiter is found
 */
function extractEmailFromContactDisplay(
  customerContactDisplayValue: string,
): string | null {
  if (customerContactDisplayValue.includes(NAME_EMAIL_DELIMITER)) {
    const parts = customerContactDisplayValue.split(NAME_EMAIL_DELIMITER);
    return parts[parts.length - 1] || null;
  }
  // If it contains @ and no delimiter, assume it's an email address
  if (customerContactDisplayValue.includes("@")) {
    return customerContactDisplayValue;
  }
  return null;
}

/**
 * Extracts the phone number from a customerContactDisplayValue that may be in the format
 * "<name> - <phone>" or just a phone number.
 *
 * This function expects the input customerContactDisplayValue to contain a valid phone number.
 * @param customerContactDisplayValue The customerContactDisplayValue that may contain a delimiter
 * @returns The phone number, or null if no phone can be extracted
 */
function extractPhoneFromContactDisplay(
  customerContactDisplayValue: string,
): string | null {
  if (customerContactDisplayValue.includes(NAME_EMAIL_DELIMITER)) {
    const parts = customerContactDisplayValue.split(NAME_EMAIL_DELIMITER);
    return parts[parts.length - 1] || null;
  }
  // Check if it looks like a phone number by ensuring it has digits
  // The actual validation happens later via convertArbitraryPhoneNumberToE164
  // This just ensures it's not obviously not a phone number
  const digitCount = (customerContactDisplayValue.match(/\d/g) || []).length;
  const hasOnlyPhoneChars = /^[\d\s()+()-]+$/.test(customerContactDisplayValue);
  // Require at least 7 digits (minimum for international numbers)
  // and only phone-like characters (digits, spaces, +, parentheses, hyphens)
  if (digitCount >= 7 && hasOnlyPhoneChars) {
    return customerContactDisplayValue;
  }
  return null;
}

const conversationForm = groupInput({
  channel: input<ConversationPlatform>(),
  customerContactDisplayValue: input<string | null>(), // Display value: "<name> - <email>" or "<name> - <phone>"
  customerEmail: input<string | null>(),
  customerPhoneNumber: input<string | null>(),
  ccEmails: input<string>({
    validator: (value: string) => {
      const errors: ValidationError[] = [];
      const emails = value ? getEmails(value) : [];
      for (const email of emails) {
        if (!emailIsValid(email)) {
          errors.push("Invalid email");
        }
      }
      if (emails.length > 48) {
        errors.push("Too many emails, max 48");
      }
      return errors;
    },
  }),
  bccEmails: input<string>({
    validator: (value: string) => {
      const errors: ValidationError[] = [];
      const emails = value ? getEmails(value) : [];
      for (const email of emails) {
        if (!emailIsValid(email)) {
          errors.push("Invalid email");
        }
      }
      if (emails.length > 48) {
        errors.push("Too many emails, max 48");
      }
      return errors;
    },
  }),
  subject: input<string>(),
  message: input<string>(),
  firstName: input<string>(),
  lastName: input<string>(),
  emailSendingAs: input<string>(),
});

export interface ShopifyCustomer {
  email: string;
  phoneNumber?: string;
  firstName: string;
  lastName: string;
  smsOptedIn: boolean;
}

export const NewMessageModal = memo(function NewMessageModal({
  open,
  setOpen,
  customerInfo,
  handleNavigate,
  showStayOption = false,
  existingConversation,
  initialConversationPlatform = ConversationPlatform.EMAIL,
  onConversationUpdated,
}: {
  open: boolean;
  setOpen(val: boolean): void;
  customerInfo?: { _id?: string; email?: string };
  handleNavigate?: ({
    conversationId,
    stayOnPage,
  }: {
    conversationId: string;
    stayOnPage?: boolean;
  }) => Promise<void>;
  showStayOption?: boolean;
  existingConversation?: ExpandedConversation;
  initialConversationPlatform?: ConversationPlatform;
  onConversationUpdated?: (conversation: ExpandedConversation) => void;
}) {
  const cursorIndexRef = useRef<number | undefined>(undefined);

  const defaults: {
    channel: ConversationPlatform;
    customerContactDisplayValue: string;
    customerEmail: string;
    customerPhoneNumber: string;
    ccEmails: string;
    bccEmails: string;
    subject: string;
    message: string;
    firstName: string;
    lastName: string;
    files: Attachment[];
    emailSendingAs: string;
  } = {
    channel: initialConversationPlatform,
    customerContactDisplayValue: "",
    customerEmail: "",
    customerPhoneNumber: "",
    ccEmails: "",
    bccEmails: "",
    subject: "",
    message: "",
    firstName: "",
    lastName: "",
    files: [],
    emailSendingAs: "",
  };
  const [customers, setCustomers] = useState<Record<string, ShopifyCustomer>>(
    {},
  );
  const [macroModalOpen, setMacroModalOpen] = useState(false);

  const [errorMessage, setErrorMessage] = useState<string>("");
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const input = useInput(conversationForm, defaults);
  const {
    channel,
    customerContactDisplayValue,
    customerEmail,
    customerPhoneNumber,
    ccEmails,
    bccEmails,
    subject,
    message,
    firstName,
    lastName,
    emailSendingAs,
  } = input.inputs;
  const team = useRequiredContext(TeamContext);
  const apiClient = useRequiredContext(RedoClientContext);
  const client = useRequiredContext(RedoMerchantClientContext);
  const user = useContext(UserContext);
  const viewReturns = useHasPermission(
    SupportPermissions.VIEW_CUSTOMER_RETURNS,
  );
  const viewOrders = useHasPermission(SupportPermissions.VIEW_CUSTOMER_ORDERS);
  const [searchString, setSearchString] = useState<string | undefined>();
  const debouncedSearch = useDebounce(searchString, 500);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [files, setFiles] = useState<Attachment[]>([]);
  const [sendPending, setSendPending] = useState<{ stay: boolean } | undefined>(
    undefined,
  );

  const [isInternalNote, setIsInternalNote] = useState(false);

  const [macroAutomations, setMacroAutomations] =
    useState<MacroAutomationsData>({});

  const [quill, setQuill] = useState<Quill | null>(null);

  const [ccEmailsInput, setCcEmailsInput] = useState<string[]>(
    getEmails(ccEmails.value),
  );
  const [ccEmailInputValue, setCcEmailInputValue] = useState<string>("");
  const [bccEmailsInput, setBccEmailsInput] = useState<string[]>(
    getEmails(bccEmails.value),
  );
  const [bccEmailInputValue, setBccEmailInputValue] = useState<string>("");
  const [availableEmails, setAvailableEmails] = useState<string[]>([]);

  const [customersLoad, doCustomersLoad] = useTriggerLoad(async (signal) => {
    if (customerInfo?._id) {
      return;
    }

    // since the search result gets the customer name prepended, split off the name before searching.
    const searchTermPieces = (debouncedSearch || "").split(
      NAME_EMAIL_DELIMITER,
    );
    let searchTerm = searchTermPieces[searchTermPieces.length - 1];

    // Format phone number search terms to improve matching
    // This helps with partial phone number searches including +1
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    const isPhoneSearch = /^\+?[0-9\s()-]+$/.test(searchTerm);
    if (isPhoneSearch) {
      // Strip non-digit characters except the leading +
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      searchTerm = searchTerm.replace(/(?!^\+)[^0-9]/g, "");
    }

    setCustomersLoading(true);
    const customers = await getCustomerSearch(client, {
      search: searchTerm || "",
      signal,
    });
    setCustomersLoading(false);
    return customers;
  });

  const specifiedCustomerLoad = useLoad(
    async (signal) => {
      if (!customerInfo?._id) {
        return;
      }

      const customer = await getCustomer(client, {
        id: customerInfo._id,
        signal,
      });

      const primaryEmail = getPrimaryCustomerEmail(customer);
      const primaryPhone = getPrimaryCustomerPhoneNumber(customer);

      if (channel.value === ConversationPlatform.SMS) {
        if (!primaryPhone) {
          throw new Error("Customer phone number not found");
        }

        const customerData = {
          email: primaryEmail || "",
          phoneNumber: primaryPhone,
          firstName: customer.firstName ?? "",
          lastName: customer.lastName ?? "",
          smsOptedIn:
            !!customer?.supportCommunicationConsent?.textMessages?.optInDate &&
            !customer.supportCommunicationConsent.textMessages.optOutDate,
        };

        setCustomers({ [primaryPhone]: customerData });

        // Construct display name directly
        const hasName = customer.firstName || customer.lastName;
        const fullName =
          `${customerData.firstName || ""} ${customerData.lastName || ""}`.trim();
        const displayName = hasName
          ? `${fullName}${NAME_EMAIL_DELIMITER}${primaryPhone}`
          : primaryPhone;

        customerContactDisplayValue.setValue(displayName);
        customerPhoneNumber.setValue(primaryPhone);
        customerEmail.setValue(primaryEmail || null);
      } else {
        if (!primaryEmail) {
          throw new Error("Customer email not found");
        }

        const customerData = {
          email: primaryEmail,
          phoneNumber: primaryPhone,
          firstName: customer.firstName ?? "",
          lastName: customer.lastName ?? "",
          smsOptedIn:
            !!customer?.supportCommunicationConsent?.textMessages?.optInDate &&
            !customer.supportCommunicationConsent.textMessages.optOutDate,
        };

        setCustomers({ [primaryEmail]: customerData });

        // Construct display name directly
        const hasName = customer.firstName || customer.lastName;
        const fullName =
          `${customerData.firstName || ""} ${customerData.lastName || ""}`.trim();
        const displayName = hasName
          ? `${fullName}${NAME_EMAIL_DELIMITER}${primaryEmail}`
          : primaryEmail;

        customerContactDisplayValue.setValue(displayName);
        customerEmail.setValue(primaryEmail);
        customerPhoneNumber.setValue(primaryPhone || null);
      }

      return customer;
    },
    [customerInfo?._id, channel.value],
  );

  const [returnsLoad, doReturnsLoad] = useTriggerLoad(async (signal) => {
    if (!viewReturns) {
      return [];
    }
    const primaryEmail = customerEmail.value;
    if (primaryEmail && emailIsValid(primaryEmail)) {
      return await getTeamReturns(client, { email: primaryEmail, signal });
    }
    return [];
  });

  const [ordersLoad, doOrdersLoad] = useTriggerLoad(async (signal) => {
    if (!viewOrders) {
      return [];
    }
    const primaryEmail = customerEmail.value;
    if (primaryEmail && emailIsValid(primaryEmail)) {
      return await getTeamOrders(client, { email: primaryEmail, signal });
    }
    return [];
  });

  const [cartsLoad, _doCartsLoad] = useTriggerLoad(async (signal) => {
    return [];
  });

  useEffect(() => {
    if (searchString !== undefined) {
      doCustomersLoad();
    }
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  useEffect(() => {
    if (open && customerInfo?.email) {
      setSearchString(customerInfo.email);
    }
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (customerEmail.value) {
      doOrdersLoad();
      doReturnsLoad();
    }
  }, [customerEmail.value, doOrdersLoad, doReturnsLoad]);

  const [emailIntegrationsLoad] = useLazyContext(EmailIntegrationsContext);

  useEffect(() => {
    if (
      quill &&
      channel.value === ConversationPlatform.EMAIL &&
      team?.settings.support?.emailSignature
    ) {
      // Add email signature to the end, and create space for new message.
      quill.clipboard.dangerouslyPasteHTML(
        "<br><br><br>" + team.settings.support.emailSignature,
      );
    }
  }, [quill, team, channel.value]);

  useEffect(() => {
    if (!emailIntegrationsLoad.value) {
      return;
    }
    appendEmails(
      [
        ...(emailIntegrationsLoad.value.customIntegrations || []),
        ...(emailIntegrationsLoad.value.gmailIntegrations || []),
        ...(emailIntegrationsLoad.value.outlookIntegrations || []),
      ].map((integration) => integration.email),
    );
    emailSendingAs.setValue(team?.settings.support?.defaultEmail || "");
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailIntegrationsLoad.value]);

  function pickDefaultEmail(availableEmails: string[]): string {
    // First, check if there's a team default email and it's available
    if (team?.settings?.support?.defaultEmail) {
      const defaultEmail = team.settings.support.defaultEmail;
      if (availableEmails.includes(defaultEmail)) {
        return defaultEmail;
      }
    }

    // Fall back to user's email if available
    const userEmail = user!.email.toLowerCase();
    const emailThatMatchesUsersEmail = availableEmails.find(
      (email) => email.toLowerCase() === userEmail,
    );
    if (emailThatMatchesUsersEmail) {
      return emailThatMatchesUsersEmail;
    }

    // Finally, fall back to the last configured email
    const firstEmailThatWasSetup = availableEmails.at(-1);
    if (firstEmailThatWasSetup) {
      return firstEmailThatWasSetup;
    }
    return "";
  }

  const appendEmails = (emails: string[]) => {
    setAvailableEmails((oldAvailableEmails: string[]) => {
      const updatedAvailableEmails = oldAvailableEmails;
      for (const email of emails || []) {
        if (!updatedAvailableEmails.includes(email)) {
          updatedAvailableEmails.push(email);
        }
      }
      return [...updatedAvailableEmails];
    });
    emailSendingAs.setValue(pickDefaultEmail(availableEmails));
  };

  const onCustomerInputChange = (event: any, value: string) => {
    setSearchString(value);
  };

  const [macrosLoad, refreshMacros] = useLazyContext(MacrosContext);

  function addAttachments(attachments: Attachment[]) {
    setFiles((oldFiles) => {
      const uniqueAttachments = unique(
        [...attachments, ...oldFiles],
        (attachment) => attachment.url,
      );
      return uniqueAttachments;
    });
  }

  const handleUpload = async ({
    event,
    file,
  }: {
    event?: any;
    file?: File;
  }) => {
    const fileToUpload = file || event.target?.files?.[0];
    if (!fileToUpload) {
      return;
    }

    const form = new FormData();
    form.append("file", fileToUpload);
    form.append("fileName", fileToUpload.name);
    const response = await uploadFile(apiClient, form);
    if (response.success) {
      const body = response.body;
      addAttachments([body]);
    } else {
      toast(conversationFileUploadErrorMessages[response.error], {
        variant: "error",
      });
    }
  };

  const handleCreateConversation = async ({
    stayOnPage,
  }: {
    stayOnPage: boolean;
  }) => {
    setSendPending({ stay: !!stayOnPage });
    if (!quill) {
      return;
    }

    let phoneNumberForBody: string | undefined;
    if (
      channel.value === ConversationPlatform.SMS &&
      customerPhoneNumber.value
    ) {
      phoneNumberForBody = convertArbitraryPhoneNumberToE164(
        customerPhoneNumber.value,
      )?.toString();
    }

    // Get customer data - try to find by email/phone first, then fall back to form values
    const emailKey = customerEmail.value || "";
    const phoneKey = customerPhoneNumber.value || "";
    const customer = customers[emailKey] || customers[phoneKey];

    const body: CreateConversationBody = {
      channel: channel.value,
      email:
        channel.value === ConversationPlatform.EMAIL
          ? customerEmail.value || undefined
          : undefined,
      phoneNumber: phoneNumberForBody,
      ccEmails: getEmails(ccEmails.value),
      bccEmails: getEmails(bccEmails.value),
      subject: subject.value,
      message: quill.getText(),
      htmlBody: quill.getSemanticHTML(),
      firstName: customer?.firstName || firstName.value,
      lastName: customer?.lastName || lastName.value,
      files,
      emailSendingAs: emailSendingAs.value,
      visibility: isInternalNote
        ? MessageVisibility.INTERNAL
        : MessageVisibility.PUBLIC,
    };

    await alertOnFailure("Failed to create ticket")(async () => {
      if (!team) {
        throw new Error("Team not found");
      }
      const createdConversation = await createConversation(client, body);

      await performMacroAutomationsAfterSendingMessage({
        client,
        automations: macroAutomations,
        conversation: createdConversation,
        team,
        shouldSetSubjectOnConversation: false,
        messageToForwardThatsAlreadySent: createdConversation.messages[0],
      });

      if (handleNavigate) {
        await handleNavigate({
          conversationId: createdConversation._id,
          stayOnPage,
        });
      }
      amplitude.logEvent("create-conversation");
      handleClose();
    }).finally(() => setSendPending(undefined));
  };

  const handleNewMessageInConversation = async () => {
    if (!quill || !existingConversation) {
      return;
    }
    setSendPending({ stay: true });

    let phoneNumberForBody: string | undefined;
    if (
      channel.value === ConversationPlatform.SMS &&
      customerPhoneNumber.value
    ) {
      phoneNumberForBody = convertArbitraryPhoneNumberToE164(
        customerPhoneNumber.value,
      )?.toString();
    }
    const body: ReplyNoDraftBody = {
      channel: channel.value,
      subject: subject.value,
      message: quill.getText(),
      email:
        channel.value === ConversationPlatform.EMAIL
          ? customerEmail.value || undefined
          : undefined,
      phoneNumber: phoneNumberForBody,
      htmlBody: quill.getSemanticHTML(),
      emailSendingAs: emailSendingAs.value,
      conversationId: existingConversation._id,
      visibility: isInternalNote
        ? MessageVisibility.INTERNAL
        : MessageVisibility.PUBLIC,
      replyType: EmailReplyType.REPLY,
      attachments: files,
      usersMentioned: [],
    };
    await alertOnFailure("Failed to send message")(async () => {
      if (!team) {
        throw new Error("Team not found");
      }
      const updatedConversation = await replyNoDraft(client, body);

      await performMacroAutomationsAfterSendingMessage({
        client,
        automations: macroAutomations,
        conversation: updatedConversation,
        team,
        shouldSetSubjectOnConversation: false,
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        messageToForwardThatsAlreadySent:
          updatedConversation.messages[updatedConversation.messages.length - 1],
      });

      onConversationUpdated?.(updatedConversation);
      amplitude.logEvent("new-message-in-modal");
      handleClose();
    }).finally(() => setSendPending(undefined));
  };

  const handleSubmit = async ({ stayOnPage }: { stayOnPage: boolean }) => {
    if (existingConversation) {
      await handleNewMessageInConversation();
    } else {
      await handleCreateConversation({ stayOnPage });
    }
  };

  const setMacro = async (macro: Macro) => {
    if (!team) {
      return;
    }
    const [contentWithVariablesReplaced, htmlContentWithVariablesReplaced] =
      await materializeMacroIntoPendingAutomations({
        client,
        team,
        existingPendingAutomations: macroAutomations,
        newMacroToInclude: macro,
        email:
          channel.value === ConversationPlatform.EMAIL
            ? customerEmail.value || ""
            : "",
        firstName:
          (customerEmail.value && customers[customerEmail.value]?.firstName) ||
          (customerPhoneNumber.value &&
            customers[customerPhoneNumber.value]?.firstName) ||
          firstName.value,
        lastName:
          (customerEmail.value && customers[customerEmail.value]?.lastName) ||
          (customerPhoneNumber.value &&
            customers[customerPhoneNumber.value]?.lastName) ||
          lastName.value,
        fullName: "",
        agentFirstName: user?.firstName || "",
        agentLastName: user?.lastName || "",
        agentFullName: user?.name || "",
        addAttachments,
        setErrorMessage,
        setShowErrorMessage,
        setMacroAutomations,
        conversationPlatform: channel.value,
        visibilityOfMessage: MessageVisibility.PUBLIC,
      });

    if (macro.emailSubjectToSet) {
      subject.setValue(macro.emailSubjectToSet);
    }
    if (quill) {
      const indexToInsert = cursorIndexRef.current ?? 0;
      if (
        channel.value === ConversationPlatform.EMAIL &&
        htmlContentWithVariablesReplaced
      ) {
        quill.clipboard.dangerouslyPasteHTML(
          indexToInsert,
          htmlContentWithVariablesReplaced,
        );
      } else if (contentWithVariablesReplaced) {
        quill.insertText(indexToInsert, contentWithVariablesReplaced);
      }

      /* Macro variables have a unique purple-on-purple color scheme. This resets the color and background color of the macro variables to the default. */
      clearFormattingFromMacroAutomationsText(quill);
    }
  };

  const handleClose = () => {
    setMacroModalOpen(false);
    setFiles([]);
    setCcEmailsInput([]);
    setCcEmailInputValue("");
    setBccEmailsInput([]);
    setBccEmailInputValue("");
    channel.setValue(ConversationPlatform.EMAIL);
    customerContactDisplayValue.setValue("");
    customerEmail.setValue("");
    customerPhoneNumber.setValue("");
    ccEmails.setValue("");
    bccEmails.setValue("");
    subject.setValue("");
    message.setValue("");
    firstName.setValue("");
    lastName.setValue("");
    setOpen(false);
  };

  useEffect(() => {
    if (customersLoad.value?.customers) {
      const newCustomers = customersLoad.value.customers.reduce(
        (accumulator: Record<string, ShopifyCustomer>, customer) => {
          if (channel.value === ConversationPlatform.EMAIL && customer.email) {
            accumulator[customer.email] = {
              email: customer.email,
              phoneNumber: customer.phone || undefined,
              firstName: customer.first_name!,
              lastName: customer.last_name!,
              smsOptedIn:
                !!customer.supportCommunicationConsent?.textMessages
                  ?.optInDate &&
                !customer.supportCommunicationConsent.textMessages.optOutDate,
            };
          }

          if (channel.value === ConversationPlatform.SMS && customer.phone) {
            accumulator[customer.phone] = {
              email: customer.email || "",
              phoneNumber: customer.phone,
              firstName: customer.first_name!,
              lastName: customer.last_name!,
              smsOptedIn:
                !!customer.supportCommunicationConsent?.textMessages
                  ?.optInDate &&
                !customer.supportCommunicationConsent.textMessages.optOutDate,
            };
          }

          return accumulator;
        },
        {},
      );

      setCustomers(newCustomers);

      if (
        !customerContactDisplayValue.value &&
        customerInfo?.email &&
        searchString === customerInfo.email
      ) {
        const displayName = getCustomerDisplayName(customerInfo.email);
        customerContactDisplayValue.setValue(displayName);
        customerEmail.setValue(customerInfo.email);
      }
    }
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customersLoad.value, channel.value]);

  const getCustomerDisplayName = (identifier?: string) => {
    if (!identifier) {
      return "";
    }

    if (identifier.includes(NAME_EMAIL_DELIMITER)) {
      return identifier;
    }

    if (!customers[identifier]) {
      return `New customer${NAME_EMAIL_DELIMITER}${identifier}`;
    }

    if (
      !customers[identifier].firstName &&
      !customers[identifier].lastName &&
      !customers[identifier].email &&
      !customers[identifier].phoneNumber
    ) {
      return "";
    }

    if (!customers[identifier]?.firstName && !customers[identifier]?.lastName) {
      if (
        channel.value === ConversationPlatform.SMS &&
        customers[identifier]?.phoneNumber
      ) {
        return customers[identifier]?.phoneNumber || "";
      } else {
        return customers[identifier]?.email || "";
      }
    }

    const contactInfo =
      channel.value === ConversationPlatform.SMS
        ? customers[identifier]?.phoneNumber || identifier
        : customers[identifier]?.email || identifier;

    const fullName = `${customers[identifier]?.firstName || ""} ${
      customers[identifier]?.lastName || ""
    }`.trim();
    return `${fullName}${NAME_EMAIL_DELIMITER}${contactInfo}`;
  };

  const prependCustom = (customerLabels: string[]) => {
    if (searchString) {
      const isEmail = searchString.includes("@");
      const isPhone = /^\+?[0-9\s()-]+$/.test(searchString);

      if (
        (channel.value === ConversationPlatform.EMAIL && isEmail) ||
        (channel.value === ConversationPlatform.SMS && isPhone)
      ) {
        return [searchString, ...customerLabels];
      }
    }
    return customerLabels;
  };

  const [submitDisabled, setSubmitDisabled] = useState(false);

  const removeFile = (url: string) => {
    setFiles((oldFiles) => {
      return oldFiles.filter((file) => file.url !== url);
    });
  };

  if (!user) {
    return null;
  }

  const showMacroAutomationsSection =
    isAtLeastOneMacroAutomationActive(macroAutomations);

  // todo add back in if we block because of opt in later
  // const disabledBecauseOfNoOptIn =
  //   channel.value === ConversationPlatform.SMS &&
  //   customers[customerContactDisplayValue.value || ""] &&
  //   !customers[customerContactDisplayValue.value || ""]?.smsOptedIn;

  const footer = (
    <Flex align="center" gap="md" justify="space-between" p="xl" w="full">
      <Flex align="center" gap="md">
        <RedoButton
          disabled={macrosLoad.pending || !!sendPending}
          hierarchy="tertiary"
          onClick={() => {
            setMacroModalOpen(true);
          }}
          size="sm"
          text="Use template"
        />
        <RedoToggle
          label="Internal note"
          setValue={setIsInternalNote}
          size="sm"
          value={isInternalNote}
        />
      </Flex>
      {showStayOption ? (
        <Flex gap="sm">
          <RedoButton
            disabled={
              !customerContactDisplayValue.value ||
              !subject.value ||
              !emailSendingAs.value ||
              submitDisabled ||
              (!!sendPending && sendPending.stay)
            }
            hierarchy="secondary"
            onClick={() => handleSubmit({ stayOnPage: false })}
            pending={!!sendPending && !sendPending.stay}
            size="md"
            text="Send and go to ticket"
          />
          <RedoButton
            disabled={
              !customerContactDisplayValue.value ||
              !subject.value ||
              !emailSendingAs.value ||
              submitDisabled ||
              (!!sendPending && !sendPending.stay)
            }
            hierarchy="primary"
            onClick={() => handleSubmit({ stayOnPage: true })}
            pending={!!sendPending && sendPending.stay}
            size="md"
            text="Send and stay here"
          />
        </Flex>
      ) : (
        <Flex align="center">
          {/* todo add back in if we block for opting in here */}
          {/* {disabledBecauseOfNoOptIn && (
              <Text textColor="error" fontSize="xs">
                Customer has not opted in to SMS. Please use email instead.
              </Text>
            )} */}
          <RedoButton
            disabled={
              !customerContactDisplayValue.value ||
              (channel.value === ConversationPlatform.EMAIL &&
                !subject.value) ||
              (channel.value === ConversationPlatform.EMAIL &&
                !emailSendingAs.value) ||
              submitDisabled
            }
            hierarchy="primary"
            onClick={() => handleSubmit({ stayOnPage: false })}
            pending={!!sendPending}
            size="md"
            text="Send"
          />
        </Flex>
      )}
    </Flex>
  );

  return (
    <>
      <RedoModal
        footerBorder
        hideFooter
        isOpen={open}
        modalSize={customerContactDisplayValue.value ? "xl" : "lg"}
        onModalCloseRequested={handleClose}
        title={existingConversation ? "New message" : "Create ticket"}
      >
        <Flex dir="row" height="full">
          <Flex
            dir="column"
            gap="lg"
            p="md"
            style={{ overflowY: "auto", flex: 1 }}
          >
            <RedoSingleSelectDropdownInput
              label="Channel"
              options={
                team.settings.support?.billing?.textMessaging?.enabled
                  ? [
                      {
                        type: "text" as const,
                        id: "email",
                        value: ConversationPlatform.EMAIL,
                        text: "Email",
                      },
                      {
                        type: "text" as const,
                        id: "sms",
                        value: ConversationPlatform.SMS,
                        text: "SMS",
                      },
                    ]
                  : [
                      {
                        type: "text" as const,
                        id: "email",
                        value: ConversationPlatform.EMAIL,
                        text: "Email",
                      },
                    ]
              }
              optionSelected={({ item }) => {
                channel.setValue(item.value);
              }}
              selectedItem={
                channel.value === ConversationPlatform.EMAIL
                  ? {
                      type: "text" as const,
                      id: "email",
                      value: ConversationPlatform.EMAIL,
                      text: "Email",
                    }
                  : channel.value === ConversationPlatform.SMS
                    ? {
                        type: "text" as const,
                        id: "sms",
                        value: ConversationPlatform.SMS,
                        text: "SMS",
                      }
                    : undefined
              }
              size="sm"
            />
            {channel.value === "email" && (
              <RedoSingleSelectDropdownInput
                label="Email to send as"
                options={availableEmails.map((email) => ({
                  type: "text" as const,
                  id: email,
                  value: email,
                  text: email,
                }))}
                optionSelected={({ item }) => {
                  emailSendingAs.setValue(item.value);
                }}
                selectedItem={
                  emailSendingAs.value
                    ? {
                        type: "text" as const,
                        id: emailSendingAs.value,
                        value: emailSendingAs.value,
                        text: emailSendingAs.value,
                      }
                    : undefined
                }
                size="sm"
              />
            )}
            {customerInfo?._id ? (
              <RedoTextInput
                label="Customer"
                setValue={() => {}}
                size="sm"
                state="readonly"
                value={
                  specifiedCustomerLoad.value
                    ? channel.value === ConversationPlatform.SMS
                      ? getCustomerDisplayName(
                          getPrimaryCustomerPhoneNumber(
                            specifiedCustomerLoad.value,
                          ),
                        )
                      : getCustomerDisplayName(
                          getPrimaryCustomerEmail(specifiedCustomerLoad.value),
                        )
                    : ""
                }
              />
            ) : (
              <Flex dir="column" gap="sm">
                <Text fontSize="sm" fontWeight="medium" textColor="primary">
                  Customer
                </Text>
                <Text fontSize="xs" textColor="secondary">
                  Type to search customers
                </Text>
                <Autocomplete
                  filterOptions={(x) => x}
                  getLabel={(customer): string => {
                    return getCustomerDisplayName(customer);
                  }}
                  keyFn={(customer, index): string => {
                    return `${getCustomerDisplayName(customer)}-${index}`;
                  }}
                  noOptionsText={
                    customersLoading ? (
                      <Flex align="center" gap="sm">
                        <RedoSpinner size="sm" />
                        <Text fontSize="sm" textColor="secondary">
                          Loading...
                        </Text>
                      </Flex>
                    ) : (
                      "No options found, search for customer by email, name, phone number, or order number"
                    )
                  }
                  onInputChange={onCustomerInputChange}
                  options={
                    !customersLoading
                      ? prependCustom(Object.keys(customers || {}))
                      : []
                  }
                  value={customerContactDisplayValue.value}
                  valueChange={(value: string | null) => {
                    if (!value) {
                      customerContactDisplayValue.setValue(null);
                      customerEmail.setValue(null);
                      customerPhoneNumber.setValue(null);
                      firstName.setValue("");
                      lastName.setValue("");
                      return;
                    }
                    customerContactDisplayValue.setValue(value);

                    // Extract email or phone based on channel
                    if (channel.value === ConversationPlatform.EMAIL) {
                      const email = extractEmailFromContactDisplay(value);
                      const emailToUse =
                        email || (emailIsValid(value) ? value : null);
                      customerEmail.setValue(emailToUse);

                      // Try to find customer data by email
                      const customer = emailToUse
                        ? customers[emailToUse]
                        : undefined;
                      if (customer) {
                        firstName.setValue(customer.firstName || "");
                        lastName.setValue(customer.lastName || "");
                        customerPhoneNumber.setValue(
                          customer.phoneNumber || null,
                        );
                      } else {
                        // Reset phone number when customer not found in EMAIL channel
                        customerPhoneNumber.setValue(null);
                        // Reset firstName/lastName to avoid stale data
                        firstName.setValue("");
                        lastName.setValue("");
                      }
                    } else {
                      // SMS channel
                      const phone = extractPhoneFromContactDisplay(value);
                      customerPhoneNumber.setValue(phone);

                      // Try to find customer data by phone
                      const customer = phone ? customers[phone] : undefined;
                      if (customer) {
                        firstName.setValue(customer.firstName || "");
                        lastName.setValue(customer.lastName || "");
                        customerEmail.setValue(customer.email || null);
                      } else {
                        // Reset email when customer not found in SMS channel
                        customerEmail.setValue(null);
                        // Reset firstName/lastName to avoid stale data
                        firstName.setValue("");
                        lastName.setValue("");
                      }
                    }
                  }}
                >
                  {(customer) => getCustomerDisplayName(customer)}
                </Autocomplete>
              </Flex>
            )}
            {customerContactDisplayValue.value &&
              !(customerEmail.value && customers[customerEmail.value]) &&
              !(
                customerPhoneNumber.value &&
                customers[customerPhoneNumber.value]
              ) && (
                <>
                  <RedoTextInput
                    errorText={firstName.allErrors[0]}
                    label="First name"
                    setValue={firstName.setValue}
                    size="sm"
                    value={firstName.value}
                  />
                  <RedoTextInput
                    errorText={lastName.allErrors[0]}
                    label="Last name"
                    setValue={lastName.setValue}
                    size="sm"
                    value={lastName.value}
                  />
                </>
              )}
            {channel.value === ConversationPlatform.EMAIL &&
              !isInternalNote && (
                <Flex dir="column" gap="sm">
                  <CollapseSubsection
                    inheritColor
                    title={
                      <Text
                        fontSize="sm"
                        fontWeight="regular"
                        textColor="secondary"
                      >
                        Cc
                      </Text>
                    }
                  >
                    <RedoTagInputField
                      delimiters={["newline", "comma", "whitespace"]}
                      description={ccEmails.allErrors[0]}
                      inputValue={ccEmailInputValue}
                      placeholder="Add CC recipients"
                      setInputValue={setCcEmailInputValue}
                      setTags={(tags) => {
                        setCcEmailsInput(tags);
                        ccEmails.setValue(tags.join(","));
                      }}
                      size="md"
                      state={
                        ccEmails.allErrors.length > 0 ? "error" : "default"
                      }
                      tags={ccEmailsInput.map((email) => ({
                        text: email,
                        theme: "neutral",
                      }))}
                    />
                  </CollapseSubsection>
                  <CollapseSubsection
                    inheritColor
                    title={
                      <Text
                        fontSize="sm"
                        fontWeight="regular"
                        textColor="secondary"
                      >
                        Bcc
                      </Text>
                    }
                  >
                    <RedoTagInputField
                      delimiters={["newline", "comma", "whitespace"]}
                      description={bccEmails.allErrors[0]}
                      inputValue={bccEmailInputValue}
                      placeholder="Add BCC recipients"
                      setInputValue={setBccEmailInputValue}
                      setTags={(tags) => {
                        setBccEmailsInput(tags);
                        bccEmails.setValue(tags.join(","));
                      }}
                      size="md"
                      state={
                        bccEmails.allErrors.length > 0 ? "error" : "default"
                      }
                      tags={bccEmailsInput.map((email) => ({
                        text: email,
                        theme: "neutral",
                      }))}
                    />
                  </CollapseSubsection>
                </Flex>
              )}
            <Divider />
            {channel.value === ConversationPlatform.EMAIL && (
              <RedoTextInput
                errorText={subject.allErrors[0]}
                label="Email subject"
                setValue={subject.setValue}
                size="md"
                value={subject.value}
              />
            )}
            <Flex dir="column" gap="xs">
              <Text fontSize="sm" textColor="secondary">
                Message
              </Text>
              <div onDrop={(e) => doFileDrop(e, handleUpload)}>
                <QuillEditor
                  cursorIndexRef={cursorIndexRef}
                  defaultHtmlContents=""
                  editorClassName={newMessageModalCss.quillEditor}
                  onQuillEditorEmptyChange={setSubmitDisabled}
                  placeholder="Start typing..."
                  readOnly={false}
                  ref={setQuill}
                  toolbarId="create-toolbar"
                />
              </div>
            </Flex>
            <QuillToolbarOptions
              additionalToolbarItems={[
                {
                  icon: PaperclipIcon,
                  buttonTitle: "Attach file",
                  identifier: "attach-file",
                  element: (
                    <QuillToolbarUploadFile
                      clearInput={(event: any) => (event.target.value = "")}
                      handleUpload={handleUpload}
                    />
                  ),
                },
              ]}
              emailMode={channel.value === ConversationPlatform.EMAIL}
              toolbarId="create-toolbar"
            />
            <QuillAttachmentCarousel
              attachments={files}
              removeFileFromDrafts={removeFile}
            />
            {showMacroAutomationsSection && (
              <Flex dir="column">
                <Text fontSize="sm" textColor="secondary">
                  Automations
                </Text>
                <MacroAutomationsList
                  macroAutomations={macroAutomations}
                  platform={channel.value}
                  setMacroAutomations={setMacroAutomations}
                />
              </Flex>
            )}
          </Flex>
          {customerContactDisplayValue.value && (
            <Flex
              borderColor="tertiary"
              borderLeftWidth="1px"
              borderStyle="solid"
              dir="column"
              p="md"
              style={{ overflowY: "auto", width: "300px", maxHeight: "70vh" }}
            >
              <ConversationDetailsOrders
                cartsLoad={cartsLoad}
                doOrdersLoad={doOrdersLoad}
                doReturnsLoad={doReturnsLoad}
                ordersLoad={ordersLoad}
                returnsLoad={returnsLoad}
              />
            </Flex>
          )}
        </Flex>
        {footer}
      </RedoModal>
      <MacroModal
        key={macrosLoad?.value?.length}
        macros={macrosLoad.value}
        open={macroModalOpen}
        refreshMacros={refreshMacros}
        setMacro={setMacro}
        setOpen={setMacroModalOpen}
      />
      {showErrorMessage && (
        <Snackbar
          autoHideDuration={10_000}
          onClose={() => setShowErrorMessage(false)}
          open={showErrorMessage}
        >
          <Alert onClose={() => setShowErrorMessage(false)} severity="error">
            {errorMessage}
          </Alert>
        </Snackbar>
      )}
    </>
  );
});
